import React, { useRef, useState } from 'react';
import { adminProductApi } from '../../../../api/adminProductApi';
import { PRODUCT_STATES } from '../constants';
import { createProductDraft, validateProductDraft } from './mobileProductUtils';
import MobileProductDialog from './MobileProductDialog';
import MobileProductConfirmation from './MobileProductConfirmation';
import MobileProductField from './MobileProductField';
import MobileProductHint from './MobileProductHint';
import MobileProductChoice from './MobileProductChoice';
import MobileProductFeedback from './MobileProductFeedback';
import MobileCategoryPicker from './MobileCategoryPicker';
import MobileBulkCreateItem, { emptyMobileCreateItem } from './MobileBulkCreateItem';
import MobileProductImport from './MobileProductImport';
import useMobileMutation from './useMobileMutation';

export function prepareMobileCreateItems(items, defaults) {
  const selected = items.filter(item => item.selected);
  if (!selected.length) throw new Error('Оберіть хоча б один товар для створення');
  const codes = new Set();
  return selected.map((item, index) => {
    const draft = createProductDraft({ ...item, ...defaults });
    const error = validateProductDraft(draft);
    if (error) throw new Error(`Товар ${index + 1}: ${error}`);
    const code = draft.productCode.trim();
    if (codes.has(code)) throw new Error(`Артикул ${code} повторюється у списку`);
    codes.add(code);
    return { ...draft, productCode: code, name: draft.name.trim() };
  });
}

export default function MobileBulkCreateProducts({
  categories,
  discountCategories,
  rates,
  resourcesError,
  onRetry,
  onSaved,
  onClose,
}) {
  const [items, setItems] = useState(() => [emptyMobileCreateItem()]);
  const [limit, setLimit] = useState(20);
  const [defaults, setDefaults] = useState({
    state: 'ACTIVE',
    categoryIds: [],
    discount: 0,
    discountCategoryName: '',
    expectedArrivalDate: '',
  });
  const [result, setResult] = useState(null);
  const [blocked, setBlocked] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const initialState = useRef(JSON.stringify({ items, defaults }));
  const mutation = useMobileMutation();
  const changeDefault = (key, value) => setDefaults(prev => ({ ...prev, [key]: value }));
  const requestClose = () => {
    if (!mutation.canClose() || parsing) return;
    if (items.length && JSON.stringify({ items, defaults }) !== initialState.current)
      setLeaving(true);
    else onClose();
  };
  const create = () => {
    if (blocked || parsing || leaving) return;
    mutation.run(
      () => adminProductApi.bulkCreateProducts(prepareMobileCreateItems(items, defaults)),
      response => {
        const outcome = response.data?.data ?? response.data;
        if (
          !outcome ||
          !Number.isInteger(outcome.created) ||
          !Number.isInteger(outcome.requested)
        ) {
          setBlocked(true);
          mutation.setError(
            'Сервер не повернув результат створення. Оновіть список товарів перед повторною спробою.',
          );
          onSaved();
          return;
        }
        setResult(outcome);
        if (outcome.created > 0) {
          const errors = outcome.errors || [];
          // Only retain explicitly failed codes: retry must not recreate successful products.
          if (
            errors.some(error => !error.productCode) ||
            Number(outcome.created) + errors.length !== Number(outcome.requested)
          )
            setBlocked(true);
          else {
            const failed = new Set(errors.map(error => error.productCode));
            setItems(prev =>
              prev.filter(item => !item.selected || failed.has(item.productCode.trim())),
            );
          }
          onSaved();
        }
      },
    );
  };
  const selectedCount = items.filter(item => item.selected).length;
  return (
    <MobileProductDialog
      title='Масове створення товарів'
      subtitle={`У списку: ${items.length} · Обрано: ${selectedCount}`}
      busy={mutation.busy || parsing}
      onClose={requestClose}
      onSubmit={create}
      footer={
        <>
          <button
            type='button'
            className='btn btn-outline-secondary'
            disabled={mutation.busy || parsing}
            onClick={requestClose}
          >
            Закрити
          </button>
          <button
            type='submit'
            className='btn btn-dark'
            disabled={
              mutation.busy || parsing || leaving || !selectedCount || blocked || !!resourcesError
            }
          >
            {mutation.busy ? 'Створення…' : `Створити (${selectedCount})`}
          </button>
        </>
      }
    >
      {leaving && (
        <MobileProductConfirmation
          message='Є незбережений список товарів. Залишити форму?'
          confirmLabel='Залишити без збереження'
          onConfirm={onClose}
          onCancel={() => setLeaving(false)}
        />
      )}
      <div hidden={leaving}>
        <MobileProductFeedback
          error={mutation.error || resourcesError}
          onRetry={resourcesError && onRetry}
          message={result && `Створено: ${result.created ?? 0} з ${result.requested ?? 0}`}
        />
        {result?.errors?.map((error, index) => (
          <p className='mp-feedback mp-feedback-error' key={index}>
            {error.productCode || 'Товар'}: {error.reason}
          </p>
        ))}
        {blocked && (
          <p className='mp-feedback mp-feedback-error'>
            Сервер повернув неповний результат. Оновіть список товарів перед наступним створенням.
          </p>
        )}
        <details className='mp-modal-section'>
          <summary>Імпорт з Excel</summary>
          <MobileProductImport
            rates={rates}
            onBusyChange={setParsing}
            disabled={mutation.busy || blocked}
            onItems={next => {
              setItems(next);
              setLimit(20);
              setResult(null);
            }}
          />
        </details>
        <details className='mp-modal-section'>
          <summary>Спільні налаштування</summary>
          <fieldset disabled={mutation.busy || blocked}>
            <MobileProductChoice
              label='Статус'
              options={PRODUCT_STATES.filter(state => state.value)}
              value={defaults.state}
              onChange={value => changeDefault('state', value)}
            />
            <MobileProductField
              label='Знижка, %'
              type='number'
              min='0'
              max='100'
              value={defaults.discount}
              onChange={value => changeDefault('discount', value)}
            />
            <MobileProductChoice
              label='Категорія знижки'
              options={[
                { value: '', label: 'Без групи знижок' },
                ...discountCategories.map(category => ({
                  value: category.name,
                  label: category.name,
                })),
              ]}
              value={defaults.discountCategoryName}
              onChange={value => changeDefault('discountCategoryName', value)}
            />
            <MobileProductField
              label='Дата надходження'
              type='date'
              value={defaults.expectedArrivalDate}
              onChange={value => changeDefault('expectedArrivalDate', value)}
            />
            <MobileCategoryPicker
              categories={categories}
              value={defaults.categoryIds}
              onChange={value => changeDefault('categoryIds', value)}
            />
          </fieldset>
        </details>
        <h3 className='mt-3'>Товари для створення</h3>
        <MobileProductHint>
          У запит потраплять усі обрані товари, включно з тими, які ще не показані нижче.
        </MobileProductHint>
        {items.slice(0, limit).map((item, index) => (
          <MobileBulkCreateItem
            key={item.key}
            item={item}
            index={index}
            disabled={mutation.busy || blocked}
            onChange={next =>
              setItems(prev => prev.map(current => (current.key === next.key ? next : current)))
            }
            onRemove={() => setItems(prev => prev.filter(current => current.key !== item.key))}
          />
        ))}
        {items.length > limit && (
          <button
            type='button'
            className='btn btn-outline-secondary mb-3'
            onClick={() => setLimit(value => value + 20)}
          >
            Показати ще 20
          </button>
        )}
        <button
          type='button'
          className='btn btn-outline-dark'
          disabled={mutation.busy || blocked}
          onClick={() => {
            setItems(prev => [...prev, emptyMobileCreateItem()]);
            setLimit(value => Math.max(value, items.length + 1));
          }}
        >
          + Додати рядок
        </button>
      </div>
    </MobileProductDialog>
  );
}
